/**
 * @file BusinessUnitDropdown.js
 * @description A dropdown component for selecting a business unit.
 */

import React, { useState, useEffect } from 'react';
import { fetchBusinessUnits } from '../../../services/orgLevelService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const BusinessUnitDropdown = ({
    className,
    inlineLabel = true,
    tenantId,
    selectedBusinessUnit,
    onBusinessUnitChange,
    isPublic = false,
    companyCode = null,
    required = false,
    errorMessage = null,
}) => {
    const [businessUnits, setBusinessUnits] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getBusinessUnits = async () => {
            setIsLoading(true);
            try {
                const data = await fetchBusinessUnits(tenantId, {
                    isPublic,
                    companyCode,
                });
                setBusinessUnits(data);
            } catch (error) {
                console.error('Could not fetch business units:', error);
                Toast.error(getText('msg_ac76c6ffea9e'));
            } finally {
                setIsLoading(false);
            }
        };

        getBusinessUnits();
    }, [tenantId, isPublic, companyCode]);

    const getDefaultLabel = () => {
        const matchingBusinessUnit = businessUnits.find(
            bu => bu.id === selectedBusinessUnit,
        );
        return matchingBusinessUnit
            ? matchingBusinessUnit.name
            : 'Select Business Unit';
    };

    const businessUnitOptions = [
        { value: null, label: getText('msg_e77f83a3d2a0') },
        ...businessUnits.map(format => ({
            value: format.id,
            label: format.name,
        })),
    ];

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            }  ${className} `}
            style={{ position: 'relative' }}
        >
            <label htmlFor="business-unit-dropdown">
                {getText('msg_48c0f715cbec')}
                {required && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="business-unit-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={businessUnitOptions}
                    value={selectedBusinessUnit}
                    onChange={onBusinessUnitChange}
                    disabled={businessUnits.length === 0 || isLoading}
                />
            </LoadingWrapper>
            {errorMessage && (
                <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </div>
    );
};

export default BusinessUnitDropdown;
